/**
 * Golden Lotus shared UI kit.
 * Every portal screen is assembled from these so spacing, motion and colour
 * stay consistent without a CSS framework.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState, forwardRef } from 'react';
import { X, Search, Inbox, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/* ── motion presets ───────────────────────────────────────────────────── */
export const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
};
export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.055 } },
};

/* ── Card ─────────────────────────────────────────────────────────────── */
export function Card({ children, style, hover = false, padding = 20, ...rest }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={hover ? { y: -3, borderColor: 'rgba(239,212,138,0.3)' } : undefined}
      transition={{ duration: 0.22 }}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        padding,
        position: 'relative',
        ...style,
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/* ── Section header ───────────────────────────────────────────────────── */
export function SectionTitle({ title, subtitle, action, allowViewer = false }) {
  // A page's primary action is normally a write, so it is hidden from the
  // read-only observer rather than left to fail against the API. `allowViewer`
  // marks the exceptions — room-partner mapping is one the observer may do.
  const { isViewer } = useAuth();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
      <div>
        <h2 style={{ fontSize: 19, fontWeight: 600 }}>{title}</h2>
        {subtitle && <p style={{ color: 'var(--text-mid)', fontSize: 13, marginTop: 4 }}>{subtitle}</p>}
      </div>
      {isViewer && !allowViewer ? null : action}
    </div>
  );
}

/* ── Button ───────────────────────────────────────────────────────────── */
const BTN_VARIANTS = {
  primary: {
    background: 'linear-gradient(135deg, var(--gold-300), var(--gold-500))',
    color: 'var(--ink-900)',
    border: '1px solid transparent',
    fontWeight: 600,
  },
  ghost: {
    background: 'var(--ink-800)',
    color: 'var(--text-hi)',
    border: '1px solid var(--border)',
  },
  outline: {
    background: 'transparent',
    color: 'var(--gold-400)',
    border: '1px solid rgba(239,212,138,0.35)',
  },
  danger: {
    background: 'var(--danger-dim)',
    color: 'var(--danger)',
    border: '1px solid rgba(248,113,113,0.3)',
  },
  subtle: {
    background: 'transparent',
    color: 'var(--text-mid)',
    border: '1px solid transparent',
  },
};

export function Button({
  children, variant = 'primary', size = 'md', icon: Icon,
  loading = false, disabled, style, ...rest
}) {
  const pad = size === 'sm' ? '7px 13px' : size === 'lg' ? '13px 26px' : '10px 18px';
  const fs = size === 'sm' ? 12.5 : size === 'lg' ? 15 : 13.5;

  return (
    <motion.button
      whileHover={disabled || loading ? undefined : { y: -1, filter: 'brightness(1.08)' }}
      whileTap={disabled || loading ? undefined : { scale: 0.975 }}
      transition={{ duration: 0.15 }}
      disabled={disabled || loading}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: pad, fontSize: fs, borderRadius: 'var(--r)',
        whiteSpace: 'nowrap', transition: 'background var(--dur), color var(--dur)',
        ...BTN_VARIANTS[variant],
        ...style,
      }}
      {...rest}
    >
      {loading
        ? <span style={{
            width: 14, height: 14, borderRadius: '50%',
            border: '2px solid currentColor', borderTopColor: 'transparent',
            animation: 'glSpin 0.7s linear infinite',
          }} />
        : Icon && <Icon size={size === 'sm' ? 14 : 16} />}
      {children}
    </motion.button>
  );
}

/* ── Inputs ───────────────────────────────────────────────────────────── */
const fieldStyle = {
  width: '100%',
  padding: '11px 13px',
  background: 'var(--ink-900)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r)',
  fontSize: 13.5,
  transition: 'border-color var(--dur), box-shadow var(--dur)',
  outline: 'none',
  // Without this the browser paints its native date/time pickers for a light
  // page: a dark calendar glyph on a dark field, and a white popup.
  colorScheme: 'dark',
};

/** Input types that open a native picker we want reachable from the whole field. */
const PICKER_TYPES = ['date', 'time', 'datetime-local', 'month', 'week'];

export function Field({ label, hint, error, required, children }) {
  return (
    <label style={{ display: 'block' }}>
      {label && (
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-mid)', marginBottom: 6, fontWeight: 500 }}>
          {label}{required && <span style={{ color: 'var(--gold-400)' }}> *</span>}
        </span>
      )}
      {children}
      {error && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--danger)', marginTop: 5 }}>{error}</span>}
      {hint && !error && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-lo)', marginTop: 5 }}>{hint}</span>}
    </label>
  );
}

/* forwardRef so callers (e.g. the hotel check-in desk) can hold focus. */
export const Input = forwardRef(function Input({ style, onClick, ...rest }, ref) {
  const isPicker = PICKER_TYPES.includes(rest.type);
  return (
    <input
      ref={ref}
      className={isPicker ? 'gl-picker' : undefined}
      style={{ ...fieldStyle, ...(isPicker ? { cursor: 'pointer' } : null), ...style }}
      onFocus={(e) => { e.target.style.borderColor = 'var(--gold-500)'; e.target.style.boxShadow = '0 0 0 3px rgba(239,212,138,0.09)'; }}
      onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
      onClick={(e) => {
        // Clicking anywhere in a date field opens the calendar, not just the
        // small glyph at its right edge.
        if (isPicker) e.currentTarget.showPicker?.();
        onClick?.(e);
      }}
      {...rest}
    />
  );
});

/**
 * Pick an existing option or type a new one. `value` is the visible text;
 * `onPick` fires with the chosen option (or null when the text is new), so the
 * caller can decide between sending an id and sending a name to create.
 */
export function Combobox({
  value, onChange, onPick, options = [], placeholder, createLabel = 'Use', style,
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  const term = (value || '').trim().toLowerCase();
  const matches = term
    ? options.filter((o) => `${o.label} ${o.sub || ''}`.toLowerCase().includes(term))
    : options;
  const exact = options.find((o) => o.label.toLowerCase() === term);
  const canCreate = Boolean(term) && !exact;
  const rows = canCreate ? [...matches, { create: true, label: value.trim() }] : matches;

  /* Close when focus leaves the whole control. */
  useEffect(() => {
    const onDown = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const choose = (row) => {
    onChange(row.label);
    onPick?.(row.create ? null : row);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && ['ArrowDown', 'Enter'].includes(e.key)) return setOpen(true);
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && rows[active]) { e.preventDefault(); choose(rows[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={boxRef} style={{ position: 'relative', ...style }}>
      <Input
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); onPick?.(null); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      {open && rows.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 5px)', left: 0, right: 0, zIndex: 60,
          maxHeight: 210, overflowY: 'auto',
          background: 'var(--ink-800)', border: '1px solid var(--border)',
          borderRadius: 'var(--r)', boxShadow: 'var(--shadow)', padding: 4,
        }}>
          {rows.map((row, i) => (
            <button
              key={row.create ? '__create' : row.value}
              type="button"
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(row)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '9px 11px', borderRadius: 'var(--r-sm)', textAlign: 'left',
                fontSize: 13, color: row.create ? 'var(--gold-300)' : 'var(--text-hi)',
                background: i === active ? 'rgba(239,212,138,0.09)' : 'transparent',
              }}
            >
              {row.create && <Plus size={13} style={{ flexShrink: 0 }} />}
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.create ? `${createLabel} "${row.label}"` : row.label}
                </span>
                {row.sub && !row.create && (
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-lo)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.sub}
                  </span>
                )}
              </span>
              {row.create && <span style={{ fontSize: 11, color: 'var(--text-lo)' }}>new</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Select({ children, style, ...rest }) {
  return (
    <select
      style={{ ...fieldStyle, cursor: 'pointer', appearance: 'none',
        backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23A8A8B3\' stroke-width=\'2.5\'><polyline points=\'6 9 12 15 18 9\'/></svg>")',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: 32,
        ...style }}
      {...rest}
    >
      {children}
    </select>
  );
}

export function Textarea({ style, rows = 3, ...rest }) {
  return <textarea rows={rows} style={{ ...fieldStyle, resize: 'vertical', fontFamily: 'inherit', ...style }} {...rest} />;
}

export function SearchInput({ value, onChange, placeholder = 'Search…', style }) {
  return (
    <div style={{ position: 'relative', ...style }}>
      <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-lo)' }} />
      <Input value={value} onChange={onChange} placeholder={placeholder} style={{ paddingLeft: 35 }} />
    </div>
  );
}

/* ── Badge ────────────────────────────────────────────────────────────── */
const TONES = {
  gold:    { bg: 'rgba(239,212,138,0.13)', fg: 'var(--gold-400)' },
  success: { bg: 'var(--ok-dim)',     fg: 'var(--ok)' },
  warning: { bg: 'var(--warn-dim)',   fg: 'var(--warn)' },
  danger:  { bg: 'var(--danger-dim)', fg: 'var(--danger)' },
  info:    { bg: 'var(--info-dim)',   fg: 'var(--info)' },
  neutral: { bg: 'var(--ink-700)',    fg: 'var(--text-mid)' },
};

export function Badge({ children, tone = 'neutral', dot = false, style }) {
  const t = TONES[tone] || TONES.neutral;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3.5px 10px', borderRadius: 999,
      background: t.bg, color: t.fg,
      fontSize: 11.5, fontWeight: 600, letterSpacing: 0.2,
      whiteSpace: 'nowrap', ...style,
    }}>
      {dot && <span style={{ width: 5, height: 5, borderRadius: '50%', background: t.fg }} />}
      {children}
    </span>
  );
}

/** Maps the API's status strings onto badge tones + readable labels. */
export const STATUS_TONE = {
  // events
  draft: 'neutral', published: 'info', live: 'success', completed: 'gold', cancelled: 'danger',
  // rooms
  vacant: 'neutral', reserved: 'warning', occupied: 'success', checked_out: 'info', maintenance: 'danger',
  // documents / travel
  pending: 'warning', submitted: 'info', verified: 'success', rejected: 'danger',
  not_started: 'neutral', docs_pending: 'warning', ticket_issued: 'success',
  checked_in: 'info', in_flight: 'info', arrived: 'success', returned: 'gold',
  // luggage
  registered: 'neutral', collected: 'info', in_transit: 'warning', delivered: 'success', missing: 'danger',
  // buses
  scheduled: 'neutral', boarding: 'warning', departed: 'info',
  // registrations
  approved: 'success', waitlisted: 'warning',
};

export const label = (s) =>
  String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export function StatusBadge({ status, ...rest }) {
  return <Badge tone={STATUS_TONE[status] || 'neutral'} dot {...rest}>{label(status)}</Badge>;
}

/* ── Stat tile ────────────────────────────────────────────────────────── */
export function Stat({ label: text, value, sub, icon: Icon, tone = 'gold', progress }) {
  const t = TONES[tone] || TONES.gold;
  return (
    <Card hover padding={18}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <p style={{ fontSize: 11.5, color: 'var(--text-mid)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>
            {text}
          </p>
          <p style={{ fontSize: 27, fontWeight: 700, marginTop: 7, letterSpacing: -0.5, fontVariantNumeric: 'tabular-nums' }}>
            {value ?? '—'}
          </p>
          {sub && <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 3 }}>{sub}</p>}
        </div>
        {Icon && (
          <div style={{
            width: 38, height: 38, borderRadius: 'var(--r)', flexShrink: 0,
            background: t.bg, display: 'grid', placeItems: 'center',
          }}>
            <Icon size={18} color={t.fg} />
          </div>
        )}
      </div>
      {progress !== undefined && (
        <div style={{ marginTop: 14, height: 5, background: 'var(--ink-700)', borderRadius: 99, overflow: 'hidden' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', background: `linear-gradient(90deg, ${t.fg}, var(--gold-300))`, borderRadius: 99 }}
          />
        </div>
      )}
    </Card>
  );
}

/* ── Table ────────────────────────────────────────────────────────────── */
export function Table({ columns, rows, onRowClick, empty = 'No records found', loading = false, keyField = '_id' }) {
  if (loading) return <TableSkeleton cols={columns.length} />;
  if (!rows?.length) return <EmptyState message={empty} />;

  return (
    <div style={{ overflowX: 'auto', borderRadius: 'var(--r-lg)', border: '1px solid var(--border)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
        <thead>
          <tr style={{ background: 'var(--ink-800)' }}>
            {columns.map((c) => (
              <th key={c.key} style={{
                textAlign: c.align || 'left', padding: '12px 15px',
                fontSize: 11, fontWeight: 600, color: 'var(--text-mid)',
                textTransform: 'uppercase', letterSpacing: 0.7,
                borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
                width: c.width,
              }}>
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <motion.tr
              key={row[keyField] || i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: Math.min(i * 0.022, 0.4) }}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{
                borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--ink-750)',
                cursor: onRowClick ? 'pointer' : 'default',
                background: 'var(--surface)',
                transition: 'background 0.16s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ink-800)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--surface)'; }}
            >
              {columns.map((c) => (
                <td key={c.key} style={{
                  padding: '13px 15px', fontSize: 13,
                  textAlign: c.align || 'left', verticalAlign: 'middle',
                }}>
                  {c.render ? c.render(row, i) : (row[c.key] ?? '—')}
                </td>
              ))}
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableSkeleton({ cols = 4, rows = 6 }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} style={{
          display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16,
          padding: '15px', borderBottom: r === rows - 1 ? 'none' : '1px solid var(--ink-750)',
        }}>
          {Array.from({ length: cols }).map((__, c) => <Shimmer key={c} height={13} />)}
        </div>
      ))}
    </div>
  );
}

export function Shimmer({ height = 14, width = '100%', radius = 6 }) {
  return (
    <div style={{
      height, width, borderRadius: radius,
      background: 'linear-gradient(90deg, var(--ink-750) 25%, var(--ink-700) 50%, var(--ink-750) 75%)',
      backgroundSize: '200% 100%',
      animation: 'glShimmer 1.5s infinite',
    }} />
  );
}

/* ── Pagination ───────────────────────────────────────────────────────── */
export function Pagination({ meta, onChange }) {
  if (!meta || meta.pages <= 1) return null;
  const { page, pages, total } = meta;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, gap: 12, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12.5, color: 'var(--text-lo)' }}>
        Page {page} of {pages} · {total} record{total === 1 ? '' : 's'}
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="ghost" size="sm" icon={ChevronLeft} disabled={page <= 1} onClick={() => onChange(page - 1)}>Prev</Button>
        <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>
          Next <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}

/* ── Empty / loading states ───────────────────────────────────────────── */
export function EmptyState({ message = 'Nothing here yet', hint, icon: Icon = Inbox, action }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '56px 24px', textAlign: 'center',
      border: '1px dashed var(--border)', borderRadius: 'var(--r-lg)',
      background: 'var(--surface)',
    }}>
      <motion.div
        animate={{ y: [0, -7, 0] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          width: 52, height: 52, borderRadius: 'var(--r-lg)',
          background: 'rgba(239,212,138,0.09)', display: 'grid', placeItems: 'center', marginBottom: 16,
        }}
      >
        <Icon size={24} color="var(--gold-500)" />
      </motion.div>
      <p style={{ fontSize: 14.5, fontWeight: 500 }}>{message}</p>
      {hint && <p style={{ fontSize: 12.5, color: 'var(--text-lo)', marginTop: 6, maxWidth: 380 }}>{hint}</p>}
      {action && <div style={{ marginTop: 18 }}>{action}</div>}
    </div>
  );
}

export function Loader({ label: text = 'Loading', full = false }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 14, padding: 48, minHeight: full ? '60vh' : undefined,
    }}>
      <div style={{ position: 'relative', width: 40, height: 40 }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          border: '2px solid var(--ink-700)', borderTopColor: 'var(--gold-400)',
          animation: 'glSpin 0.85s linear infinite',
        }} />
      </div>
      <span style={{ fontSize: 12.5, color: 'var(--text-lo)', letterSpacing: 0.4 }}>{text}…</span>
    </div>
  );
}

/* ── Modal ────────────────────────────────────────────────────────────── */
export function Modal({ open, onClose, title, subtitle, children, footer, width = 560 }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 900,
            background: 'rgba(4,4,6,0.75)', backdropFilter: 'blur(5px)',
            display: 'grid', placeItems: 'center', padding: 20, overflowY: 'auto',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 22, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: width, background: 'var(--ink-850)',
              border: '1px solid var(--border)', borderRadius: 'var(--r-xl)',
              boxShadow: 'var(--shadow-lg)', overflow: 'hidden', margin: 'auto',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16,
              padding: '20px 24px', borderBottom: '1px solid var(--border)',
            }}>
              <div>
                <h3 style={{ fontSize: 16.5, fontWeight: 600 }}>{title}</h3>
                {subtitle && <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 4 }}>{subtitle}</p>}
              </div>
              <button onClick={onClose} aria-label="Close"
                style={{ color: 'var(--text-lo)', display: 'flex', padding: 4, borderRadius: 6 }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: 24, maxHeight: '68vh', overflowY: 'auto' }}>{children}</div>
            {footer && (
              <div style={{
                display: 'flex', justifyContent: 'flex-end', gap: 10,
                padding: '16px 24px', borderTop: '1px solid var(--border)', background: 'var(--ink-800)',
              }}>
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ── Tabs ─────────────────────────────────────────────────────────────── */
export function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{
      display: 'flex', gap: 4, padding: 4, marginBottom: 20,
      background: 'var(--ink-800)', border: '1px solid var(--border)',
      borderRadius: 'var(--r)', overflowX: 'auto',
    }}>
      {tabs.map((t) => {
        const on = active === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            style={{
              position: 'relative', padding: '8px 15px', fontSize: 13,
              fontWeight: on ? 600 : 500,
              color: on ? 'var(--ink-900)' : 'var(--text-mid)',
              borderRadius: 'var(--r-sm)', whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', gap: 7,
            }}
          >
            {on && (
              <motion.span
                layoutId="gl-tab-pill"
                transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                style={{
                  position: 'absolute', inset: 0, borderRadius: 'var(--r-sm)',
                  background: 'linear-gradient(135deg, var(--gold-300), var(--gold-500))',
                }}
              />
            )}
            <span style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 7 }}>
              {t.icon && <t.icon size={14} />}
              {t.label}
              {t.count !== undefined && (
                <span style={{
                  fontSize: 10.5, padding: '1px 6px', borderRadius: 99, fontVariantNumeric: 'tabular-nums',
                  background: on ? 'rgba(11,11,12,0.18)' : 'var(--ink-700)',
                  color: on ? 'var(--ink-900)' : 'var(--text-mid)',
                }}>
                  {t.count}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Avatar ───────────────────────────────────────────────────────────── */
export function Avatar({ name = '', src, size = 34, vip = false }) {
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: src ? `url(${src}) center/cover` : 'linear-gradient(135deg, var(--ink-700), var(--ink-600))',
        border: vip ? '1.5px solid var(--gold-400)' : '1px solid var(--border)',
        display: 'grid', placeItems: 'center',
        fontSize: size * 0.36, fontWeight: 600,
        color: vip ? 'var(--gold-400)' : 'var(--text-mid)',
      }}>
        {!src && (initials || '?')}
      </div>
      {vip && (
        <span style={{
          position: 'absolute', bottom: -2, right: -2,
          width: 12, height: 12, borderRadius: '50%',
          background: 'var(--gold-400)', border: '2px solid var(--ink-850)',
        }} />
      )}
    </div>
  );
}

/* ── Person cell (used in most tables) ────────────────────────────────── */
export function PersonCell({ person }) {
  if (!person) return '—';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <Avatar name={person.name} src={person.avatar} vip={person.isVip} size={32} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {person.name}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-lo)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {person.designation || person.employeeCode || person.mobile || ''}
        </div>
      </div>
    </div>
  );
}

/* ── Confirm dialog ───────────────────────────────────────────────────── */
export function ConfirmModal({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', danger = false, loading }) {
  return (
    <Modal
      open={open} onClose={onClose} title={title} width={420}
      footer={
        <>
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      <p style={{ fontSize: 13.5, color: 'var(--text-mid)', lineHeight: 1.65 }}>{message}</p>
    </Modal>
  );
}
